import React, { useState, useRef } from "react";
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
    ModalBody, ModalFooter,
    VStack, HStack, Flex, Box, Text, Button, FormControl, FormLabel,
    FormHelperText, Select, Textarea, Input, Badge, Icon,
    useToast, Divider,
} from "@chakra-ui/react";
import { MdUploadFile, MdClose, MdWarning } from "react-icons/md";
import axios from "axios";
import { BASE_URL } from "config/api";
import MissionSearchInput from "components/MissionSearchInput";

const TYPES_RECLAMATION = ["Delai", "Erreur medicale", "Communication", "Materiel", "Autre"];
const NIVEAUX_GRAVITE   = ["Faible", "Moyen", "Critique"];
const SERVICES          = ["Ambulancier", "Infirmier", "Regulateur", "Medecin"];
const MAX_FILE_SIZE     = 5 * 1024 * 1024; // 5 Mo
const MAX_FILES         = 5;

const graviteColor = { Critique: "red", Moyen: "orange", Faible: "green" };

/** Convertit un File en base64 string (sans le préfixe data:...) */
const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const CreateReclamationModal = ({ isOpen, onClose, onCreated, avisId, missionId: missionIdProp }) => {
    const toast = useToast();

    const [form, setForm] = useState({
        type_reclamation : "",
        niveau_gravite   : "",
        service_concerne : "",
        description      : "",
        reference_dossier: missionIdProp ? `Mission #${missionIdProp}` : "",
    });
    const [selectedMissionId, setSelectedMissionId] = useState(missionIdProp || null);
    const [fichiers, setFichiers]   = useState([]); // [{ file, name, size, tooLarge }]
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef(null);

    const descLen = form.description.length;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleMissionSelect = (mission) => {
        if (mission) {
            setSelectedMissionId(mission.id);
            setForm((prev) => ({ ...prev, reference_dossier: `Mission #${mission.id}` }));
        } else {
            setSelectedMissionId(null);
            setForm((prev) => ({ ...prev, reference_dossier: "" }));
        }
    };

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files || []);
        const combined = [...fichiers, ...selected.map((f) => ({
            file    : f,
            name    : f.name,
            size    : f.size,
            tooLarge: f.size > MAX_FILE_SIZE,
        }))].slice(0, MAX_FILES);
        setFichiers(combined);
        e.target.value = "";
    };

    const removeFile = (idx) => setFichiers((prev) => prev.filter((_, i) => i !== idx));

    const validate = () => {
        if (!form.type_reclamation) { toast({ title: "Type de réclamation requis", status: "error", duration: 3000 }); return false; }
        if (!form.niveau_gravite)   { toast({ title: "Niveau de gravité requis",   status: "error", duration: 3000 }); return false; }
        if (!form.service_concerne) { toast({ title: "Service concerné requis",    status: "error", duration: 3000 }); return false; }
        if (!form.description.trim()) { toast({ title: "Description requise", status: "error", duration: 3000 }); return false; }
        if (fichiers.some((f) => f.tooLarge)) { toast({ title: "Un ou plusieurs fichiers dépassent 5 Mo", status: "error", duration: 3000 }); return false; }
        return true;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setIsLoading(true);
        try {
            // Convertir les fichiers en base64
            const pieces_jointes = await Promise.all(
                fichiers.map(async (f) => ({
                    nom_fichier   : f.name,
                    type_mime     : f.file.type || "application/octet-stream",
                    donnees_base64: await fileToBase64(f.file),
                }))
            );

            const payload = {
                ...form,
                avis_id  : avisId            || null,
                mission_id: selectedMissionId || null,
                pieces_jointes,
            };

            const res = await axios.post(`${BASE_URL}/reclamations`, payload, { withCredentials: true });

            toast({
                title      : "Réclamation créée",
                description: `Numéro : ${res.data.numero_reclamation}`,
                status     : "success",
                duration   : 4000,
                isClosable : true,
            });

            // Reset
            setForm({ type_reclamation: "", niveau_gravite: "", service_concerne: "", description: "", reference_dossier: "" });
            setFichiers([]);
            setSelectedMissionId(null);
            onCreated && onCreated(res.data);
            onClose();
        } catch (err) {
            toast({
                title      : "Erreur",
                description: err.response?.data?.error || "Impossible de créer la réclamation",
                status     : "error",
                duration   : 4000,
                isClosable : true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setForm({ type_reclamation: "", niveau_gravite: "", service_concerne: "", description: "", reference_dossier: missionIdProp ? `Mission #${missionIdProp}` : "" });
            setFichiers([]);
            setSelectedMissionId(missionIdProp || null);
            onClose();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose}>
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
            <ModalContent
                boxShadow="2xl"
                maxH="100vh"
                h="100vh"
                overflow="hidden"
                display="flex"
                flexDirection="column"
                position="fixed"
                right={0}
                top={0}
                bottom={0}
                m={0}
                maxW="780px"
                w="780px"
                borderRadius="0"
                borderTopLeftRadius="12px"
                borderBottomLeftRadius="12px"
            >
                <ModalHeader fontSize="xl" fontWeight="bold" py={4} px={6} borderBottom="1px solid" borderColor="gray.200" bg="white" borderTopLeftRadius="12px">
                    Nouvelle réclamation
                </ModalHeader>
                <ModalCloseButton top={4} right={4} borderRadius="full" _hover={{ bg: 'gray.100' }} isDisabled={isLoading} />

                <ModalBody p={6} flex={1} overflowY="auto">
                    <VStack spacing={5} align="stretch">

                        {/* Type + Gravité */}
                        <HStack spacing={4} align="start">
                            <FormControl isRequired flex={1}>
                                <FormLabel fontSize="sm" fontWeight="semibold">Type de réclamation</FormLabel>
                                <Select
                                    name="type_reclamation"
                                    value={form.type_reclamation}
                                    onChange={handleChange}
                                    placeholder="Sélectionner..."
                                    size="md"
                                >
                                    {TYPES_RECLAMATION.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl isRequired flex={1}>
                                <FormLabel fontSize="sm" fontWeight="semibold">Niveau de gravité</FormLabel>
                                <Select
                                    name="niveau_gravite"
                                    value={form.niveau_gravite}
                                    onChange={handleChange}
                                    placeholder="Sélectionner..."
                                    size="md"
                                >
                                    {NIVEAUX_GRAVITE.map((n) => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </Select>
                                {form.niveau_gravite && (
                                    <HStack mt={1}>
                                        <Badge colorScheme={graviteColor[form.niveau_gravite]} variant="subtle" borderRadius="full" px={2}>
                                            {form.niveau_gravite}
                                        </Badge>
                                    </HStack>
                                )}
                            </FormControl>
                        </HStack>

                        {/* Service concerné */}
                        <FormControl isRequired>
                            <FormLabel fontSize="sm" fontWeight="semibold">Service concerné</FormLabel>
                            <Select
                                name="service_concerne"
                                value={form.service_concerne}
                                onChange={handleChange}
                                placeholder="Sélectionner le service..."
                                size="md"
                            >
                                {SERVICES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Référence dossier — recherche mission */}
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="semibold">
                                Référence dossier
                                <Text as="span" color="gray.400" fontWeight="normal" ml={1}>(optionnel)</Text>
                            </FormLabel>
                            <MissionSearchInput
                                value={selectedMissionId}
                                onChange={handleMissionSelect}
                                placeholder="Rechercher une mission par numéro, date, type..."
                            />
                        </FormControl>

                        {/* Description */}
                        <FormControl isRequired>
                            <FormLabel fontSize="sm" fontWeight="semibold">Description</FormLabel>
                            <Textarea
                                name="description"
                                value={form.description}
                                onChange={handleChange}
                                placeholder="Décrivez la réclamation en détail..."
                                rows={5}
                                resize="vertical"
                            />
                            <Flex justify="flex-end" mt={1}>
                                <Text fontSize="xs" color="gray.400">{descLen} caractères</Text>
                            </Flex>
                        </FormControl>

                        <Divider />

                        {/* Pièces jointes */}
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="semibold">
                                Pièces jointes <Text as="span" color="gray.400" fontWeight="normal">(max {MAX_FILES} fichiers, 5 Mo chacun)</Text>
                            </FormLabel>

                            <input
                                type="file"
                                multiple
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                onChange={handleFileChange}
                                disabled={fichiers.length >= MAX_FILES}
                            />

                            <Button
                                leftIcon={<Icon as={MdUploadFile} />}
                                variant="outline"
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                                isDisabled={fichiers.length >= MAX_FILES}
                                colorScheme="gray"
                            >
                                Ajouter des fichiers
                            </Button>

                            {fichiers.length > 0 && (
                                <VStack align="stretch" mt={3} spacing={2}>
                                    {fichiers.map((f, idx) => (
                                        <Flex
                                            key={idx}
                                            align="center"
                                            justify="space-between"
                                            p={2}
                                            bg={f.tooLarge ? "red.50" : "gray.50"}
                                            borderRadius="8px"
                                            border="1px solid"
                                            borderColor={f.tooLarge ? "red.200" : "gray.200"}
                                        >
                                            <HStack spacing={2} flex={1} minW={0}>
                                                {f.tooLarge && <Icon as={MdWarning} color="red.500" flexShrink={0} />}
                                                <Text fontSize="xs" fontWeight="medium" noOfLines={1} color={f.tooLarge ? "red.600" : "gray.700"}>
                                                    {f.name}
                                                </Text>
                                                <Text fontSize="xs" color="gray.400" flexShrink={0}>
                                                    ({(f.size / 1024).toFixed(0)} Ko)
                                                </Text>
                                            </HStack>
                                            <Icon
                                                as={MdClose}
                                                cursor="pointer"
                                                color="gray.400"
                                                _hover={{ color: "red.500" }}
                                                onClick={() => removeFile(idx)}
                                                flexShrink={0}
                                                ml={2}
                                            />
                                        </Flex>
                                    ))}
                                </VStack>
                            )}
                        </FormControl>

                        {/* Lien avis */}
                        {avisId && (
                            <Box bg="blue.50" p={3} borderRadius="8px" borderLeft="3px solid" borderColor="blue.400">
                                <Text fontSize="xs" color="blue.700">Liée à l'avis #{avisId}</Text>
                            </Box>
                        )}
                    </VStack>
                </ModalBody>

                <ModalFooter py={4} px={6} borderTop="1px solid" borderColor="gray.200" bg="gray.50" gap={3}>
                    <Button variant="outline" onClick={handleClose} isDisabled={isLoading} size="md" px={6}>
                        Annuler
                    </Button>
                    <Button
                        colorScheme="red"
                        onClick={handleSubmit}
                        isLoading={isLoading}
                        loadingText="Création..."
                        size="md"
                        px={6}
                    >
                        Créer la réclamation
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CreateReclamationModal;
